import { create } from 'zustand';
import { postService } from '../services/postService';
import { aiService } from '../services/aiService';
import { getUserTimezone } from '../utils/format';

export const usePostsStore = create((set, get) => ({
  posts: [],
  loading: false,
  error: null,

  // --- Failure & Reliability Center --------------------------------------
  failures: [],
  failuresLoading: false,
  failuresFilter: null, // category slug or null for "all"

  // --- AI state (hashtags + best time) -----------------------------------
  hashtags: [],
  selectedHashtags: [],
  bestTime: null,
  alternatives: [],
  loadingAI: false,

  // --- Post CRUD ---------------------------------------------------------
  // Last filters applied — other components can call refresh() after
  // mutating without having to remember what was filtered.
  postsFilter: {},

  fetchPosts: async (filters = {}) => {
    set({ loading: true, error: null, postsFilter: filters });
    try {
      const posts = await postService.list(filters);
      set({ posts, loading: false });
    } catch (err) {
      set({ loading: false, error: err?.message || 'Failed to load posts' });
    }
  },

  createPost: async (payload) => {
    const newPost = await postService.create(payload);
    set((state) => ({ posts: [newPost, ...state.posts] }));
    return newPost;
  },

  updatePost: async (id, patch) => {
    const updated = await postService.update(id, patch);
    set((state) => ({
      posts: state.posts.map((p) => (p.id === id ? updated : p)),
    }));
    return updated;
  },

  reschedulePost: async (id, scheduledAt) => {
    const updated = await postService.reschedule(id, scheduledAt);
    set((state) => ({
      posts: state.posts.map((p) => (p.id === id ? updated : p)),
    }));
    return updated;
  },

  deletePost: async (id) => {
    await postService.remove(id);
    set((state) => ({ posts: state.posts.filter((p) => p.id !== id) }));
  },

  /** Queue a publish-now for a post (bypasses the scheduler). */
  publishNow: async (id) => {
    await postService.publishNow(id);
    // Optimistically update the local status so the UI reflects it before
    // the worker actually publishes.
    set((state) => ({
      posts: state.posts.map((p) =>
        p.id === id ? { ...p, status: 'scheduled' } : p
      ),
    }));
  },

  /** Retry a failed post. */
  retryPost: async (id) => {
    await postService.retry(id);
    set((state) => ({
      posts: state.posts.map((p) =>
        p.id === id ? { ...p, status: 'scheduled', lastError: null } : p
      ),
      // Optimistically drop this post's rows from the failures list.
      failures: state.failures.filter((f) => f.post_id !== id),
    }));
  },

  // --- Failure & Reliability Center --------------------------------------
  fetchFailures: async (filter = undefined) => {
    set({ failuresLoading: true });
    try {
      const category = filter === undefined ? get().failuresFilter : filter;
      const list = await postService.listFailures(
        category ? { category } : {}
      );
      set({ failures: list, failuresLoading: false, failuresFilter: category ?? null });
    } catch (err) {
      set({
        failuresLoading: false,
        error: err?.response?.data?.message || err?.message || 'Failed to load failures',
      });
    }
  },

  setFailuresFilter: (category) => {
    set({ failuresFilter: category || null });
    get().fetchFailures(category || null);
  },

  // --- AI actions --------------------------------------------------------
  generateHashtags: async (caption, platform) => {
    set({ loadingAI: true });
    try {
      const { hashtags } = await aiService.generateHashtags(caption, platform);
      set({ hashtags, selectedHashtags: hashtags, loadingAI: false });
      return hashtags;
    } catch (err) {
      set({ loadingAI: false });
      throw err;
    }
  },

  fetchBestTime: async (platform, timezone) => {
    set({ loadingAI: true });
    try {
      const tz = timezone || getUserTimezone();
      const { best_time, alternatives } = await aiService.getBestTime(platform, tz);
      set({ bestTime: best_time, alternatives, loadingAI: false });
      return { best_time, alternatives };
    } catch (err) {
      set({ loadingAI: false });
      throw err;
    }
  },

  /** Overwrite the selected hashtags — used when applying a template. */
  setSelectedHashtags: (tags) =>
    set({ hashtags: tags, selectedHashtags: tags }),

  toggleHashtag: (tag) => {
    const { selectedHashtags } = get();
    const exists = selectedHashtags.includes(tag);
    set({
      selectedHashtags: exists
        ? selectedHashtags.filter((t) => t !== tag)
        : [...selectedHashtags, tag],
    });
  },

  resetAI: () =>
    set({
      hashtags: [],
      selectedHashtags: [],
      bestTime: null,
      alternatives: [],
      loadingAI: false,
    }),
}));
